import React, { createContext, useContext, useState } from 'react';

const AuthConfigContext = createContext(undefined);

const DEFAULT_AUTH_CONFIG = {
  guest_checkout: false,
  anonymous_guest: false,
  email_password: true,
  google: false,
  facebook: false,
  apple: false,
  microsoft: false,
  enterprise_sso: false
};

export function AuthConfigProvider({ children }) {
  const [authConfig, setAuthConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('auth_config');
      if (saved) return JSON.parse(saved);
      const siteDetails = localStorage.getItem('site_details');
      if (siteDetails) {
        const parsed = JSON.parse(siteDetails);
        return {
          email_password: parsed.isEmailAuth !== false,
          google: !!parsed.isGoogleAuth,
          facebook: !!parsed.isFacebookAuth,
          apple: !!parsed.isAppleAuth,
          microsoft: !!parsed.isMicrosoftAuth,
          enterprise_sso: !!parsed.isEnterpriseSso,
          guest_checkout: !!parsed.allowGuestCheckout,
          anonymous_guest: !!parsed.isAnonymousGuestCheckout,
        };
      }
    } catch {}
    return DEFAULT_AUTH_CONFIG;
  });
  const [socialLoginUrls, setSocialLoginUrls] = useState({});
  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem('auth_config') && !localStorage.getItem('site_details');
    } catch {
      return true;
    }
  });

  // Called by the config loader when SiteDetails are fetched
  const populateFromSiteDetails = (siteDetails) => {
    if (!siteDetails) return;
    const config = {
      email_password: siteDetails.isEmailAuth !== false,
      google: !!siteDetails.isGoogleAuth,
      facebook: !!siteDetails.isFacebookAuth,
      apple: !!siteDetails.isAppleAuth,
      microsoft: !!siteDetails.isMicrosoftAuth,
      enterprise_sso: !!siteDetails.isEnterpriseSso,
      guest_checkout: !!siteDetails.allowGuestCheckout,
      anonymous_guest: !!siteDetails.isAnonymousGuestCheckout,
    };
    setAuthConfig(config);
    try {
      localStorage.setItem('auth_config', JSON.stringify(config));
    } catch {}
    setSocialLoginUrls({
      facebook: siteDetails.facebookLoginUrl || null,
      google: siteDetails.googlePlusLoginUrl || null,
      apple: siteDetails.appleLoginUrl || null,
      microsoft: siteDetails.microsoftLoginUrl || null,
      enterprise_sso: siteDetails.enterpriseSsoLoginUrl || null,
    });
    setLoading(false);
  };

  const updateAuthConfig = (newConfig) => {
    setAuthConfig(newConfig);
    return { success: true };
  };

  const isGuestCheckoutEnabled = () => authConfig.guest_checkout;

  // True when the org can use the anonymous flow (guest created on payment
  // success from payment billing details) instead of the pre-payment contact step.
  const isAnonymousGuestCheckoutEnabled = () => Boolean(authConfig.guest_checkout && authConfig.anonymous_guest !== false);

  const isProviderEnabled = (provider) => authConfig[provider] ?? false;

  const getEnabledProviders = () => {
    return Object.entries(authConfig)
      .filter(([key, value]) => key !== 'guest_checkout' && value === true)
      .map(([key]) => key);
  };

  const hasAnyAuthMethod = () => {
    return authConfig.guest_checkout || getEnabledProviders().length > 0;
  };

  const getSocialLoginUrl = (provider) => socialLoginUrls[provider] || null;

  const contextValue = {
    authConfig,
    loading,
    updateAuthConfig,
    populateFromSiteDetails,
    isGuestCheckoutEnabled,
    isAnonymousGuestCheckoutEnabled,
    isProviderEnabled,
    getEnabledProviders,
    hasAnyAuthMethod,
    getSocialLoginUrl,
    socialLoginUrls,
  };

  return (
    <AuthConfigContext.Provider value={contextValue}>
      {children}
    </AuthConfigContext.Provider>
  );
}

export function useAuthConfig() {
  const context = useContext(AuthConfigContext);
  if (context === undefined) {
    throw new Error('useAuthConfig must be used within an AuthConfigProvider');
  }
  return context;
}
